import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Shield, ShieldAlert, Database, Loader2, CheckCircle2, AlertTriangle,
  Sparkles, Hammer, Mail, Stethoscope, Search
} from 'lucide-react';

// Dedicated Admin Tools surface — one-click maintenance jobs that previously
// required SSH into the production pod. Master-admin only.
export default function AdminToolsPage() {
  const { api, user } = useAuth();
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});

  if (user?.role !== 'master_admin') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">Master Admin only.</p>
        </div>
      </div>
    );
  }

  const run = async (key, endpoint, label) => {
    if (!window.confirm(`Run "${label}"? Safe to retry — idempotent.`)) return;
    setRunning(key);
    setResults(prev => ({ ...prev, [key]: { state: 'running' } }));
    try {
      const res = await api.post(endpoint);
      setResults(prev => ({ ...prev, [key]: { state: 'ok', data: res.data } }));
    } catch (e) {
      setResults(prev => ({
        ...prev,
        [key]: { state: 'error', error: e?.response?.data?.detail || e?.message || 'Failed' },
      }));
    }
    setRunning(null);
  };

  const Tool = ({ keyName, icon: Icon, title, blurb, endpoint, danger = false }) => {
    const r = results[keyName] || {};
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={`tool-${keyName}`}>
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-9 h-9 rounded-sm flex items-center justify-center border ${danger ? 'bg-rose-500/10 text-rose-300 border-rose-500/30' : 'bg-violet-500/10 text-violet-300 border-violet-500/30'}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm text-white font-semibold">{title}</h3>
            <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{blurb}</p>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={() => run(keyName, endpoint, title)}
                disabled={!!running}
                data-testid={`run-${keyName}`}
                className={`h-8 px-3 text-[11px] font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50 ${danger ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
              >
                {running === keyName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hammer className="w-3.5 h-3.5" />}
                {running === keyName ? 'Running…' : 'Run Now'}
              </button>
              {r.state === 'ok' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-950/40 border border-emerald-500/30 px-2 h-7 rounded-sm" data-testid={`result-${keyName}-ok`}>
                  <CheckCircle2 className="w-3 h-3" /> Done
                </span>
              )}
              {r.state === 'error' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-2 h-7 rounded-sm" data-testid={`result-${keyName}-error`}>
                  <AlertTriangle className="w-3 h-3" /> {r.error}
                </span>
              )}
            </div>
            {r.state === 'ok' && r.data && (
              <pre className="mt-3 bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-[10px] font-mono text-zinc-300 overflow-x-auto whitespace-pre-wrap leading-relaxed">
{JSON.stringify(r.data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">Admin Tools</h1>
            <p className="text-xs text-zinc-500 mt-1">One-click maintenance jobs — safe, idempotent, master-admin only.</p>
          </div>
        </div>

        <Tool
          keyName="strip-submitter-emails"
          icon={Mail}
          title="Strip Submitter Emails (Privacy Backfill)"
          endpoint="/admin/migrate/strip-submitter-emails"
          blurb="Old review submissions stored the SUBMITTER'S EMAIL in the `submitter_name` field — clients viewing the review tab could see internal team emails. Run once after the privacy patch is deployed. Sweeps every doc, replaces email-shaped names with safe display names + populates @handles. Idempotent."
        />

        <Tool
          keyName="migrate-avatars-cloud"
          icon={Database}
          title="Migrate Avatars to Cloud Storage"
          endpoint="/admin/migrate-avatars-to-cloud"
          blurb="Existing user avatars stored as Base64 in MongoDB are migrated to Cloudflare R2 (S3). Drops payload size of /api/users from ~4MB → ~50KB. Idempotent — only converts entries still in Base64 format."
        />

        <Tool
          keyName="kick-backfill"
          icon={Sparkles}
          title="Kick NAS Backfill Worker"
          endpoint="/nas/backfill/kick"
          blurb="Wakes the background NAS backfill worker NOW — pushes any files whose original dual-save to NAS failed (bridge offline, no mapping, etc.). Useful right after fixing a project's NAS mapping or restoring a daemon."
        />

        <BackfillInvoiceTool api={api} />

        <VendorBillingProfileEditor api={api} />

        <NasFlowDiagnostic api={api} />
        <SubmissionRecategoriser api={api} />
        <AnimationWipeTool api={api} />
      </div>
    </div>
  );
}


// ───── Wipe Layout & Animation submissions (clean-slate before Phase 2 MY TASKS rollout)
function AnimationWipeTool({ api }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteCloud, setDeleteCloud] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/projects')
      .then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || [])))
      .catch(() => setProjects([]));
  }, [api]);

  const runDryRun = async () => {
    if (!projectId) { setErr('Pick a project first'); return; }
    setBusy(true); setErr(''); setPreview(null); setConfirmText('');
    try {
      const r = await api.post('/admin/cleanup/wipe-animation-submissions', {
        project_id: projectId, dry_run: true,
      });
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Dry-run failed');
    }
    setBusy(false);
  };

  const runExecute = async () => {
    if (!preview) return;
    const expected = `WIPE ${preview.submissions_to_wipe} ANIMATION SUBMISSIONS`;
    if (confirmText !== expected) {
      setErr(`Type "${expected}" exactly to confirm`); return;
    }
    setBusy(true); setErr('');
    try {
      const r = await api.post('/admin/cleanup/wipe-animation-submissions', {
        project_id: projectId,
        dry_run: false,
        delete_cloud_files: deleteCloud,
      });
      setPreview(r.data);
      setConfirmText('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Execute failed');
    }
    setBusy(false);
  };

  const isWiped = preview?.status === 'wiped';
  const project = projects.find(p => p.id === projectId);

  return (
    <div className="bg-zinc-900 border border-rose-500/30 rounded-sm p-4 col-span-1 md:col-span-2" data-testid="tool-wipe-animation">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center border bg-rose-500/10 text-rose-300 border-rose-500/30">
          ⚠
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-white font-semibold">Wipe Layout & Animation Submissions (Clean-Slate)</h3>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            DESTRUCTIVE. Deletes every Layout & Animation review submission on the chosen project so artists can re-upload with strict canonical filenames via the new MY TASKS flow.
            A full JSON backup is written to cloud storage <span className="font-mono">vos/_backups/animation-wipe-&lt;project_id&gt;-&lt;ts&gt;.json</span> BEFORE any delete — refuses to proceed if the backup fails.
            Dry-run is mandatory before the real delete.
          </p>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <select
              value={projectId}
              onChange={e => { setProjectId(e.target.value); setPreview(null); setConfirmText(''); }}
              data-testid="wipe-anim-project"
              className="h-8 px-2 text-[11px] rounded-sm bg-zinc-950 border border-zinc-700 text-zinc-200"
            >
              <option value="">— Pick a project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={runDryRun}
              disabled={!projectId || busy}
              data-testid="wipe-anim-dryrun"
              className="h-8 px-3 text-[11px] font-semibold rounded-sm bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50"
            >
              {busy && !preview ? 'Counting…' : 'Step 1: Dry-run preview'}
            </button>
          </div>

          {preview && !isWiped && (
            <div className="mt-3 border border-rose-500/30 rounded-sm bg-rose-500/5 p-3 text-[11px]" data-testid="wipe-anim-preview">
              <p className="text-zinc-200 font-semibold mb-2">📋 Will delete on "{project?.name}":</p>
              {Array.isArray(preview.resolved_project_ids) && preview.resolved_project_ids.length > 1 && (
                <p className="text-amber-300 text-[10px] mb-2">
                  Resolved show → episode hierarchy: {preview.resolved_project_ids.length} project IDs in scope
                </p>
              )}
              <ul className="text-zinc-300 space-y-1 ml-3 list-disc">
                <li><span className="font-mono text-rose-200">{preview.submissions_to_wipe}</span> Layout & Animation submissions</li>
                <li><span className="font-mono text-rose-200">{preview.media_files_to_wipe}</span> media files referenced</li>
                {Object.entries(preview.by_stage || {}).map(([k, v]) => (
                  <li key={k}><span className="text-zinc-400 font-mono">{k}:</span> {v}</li>
                ))}
              </ul>
              {Object.keys(preview.by_artist || {}).length > 0 && (
                <p className="text-zinc-400 mt-2"><span className="text-zinc-500">From:</span> {Object.entries(preview.by_artist).map(([a, c]) => `@${a} (${c})`).join(', ')}</p>
              )}

              {preview.submissions_to_wipe === 0 ? (
                <p className="text-emerald-300 mt-3">✓ Nothing to wipe — this project has no Layout & Animation submissions.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-zinc-300">
                    <input type="checkbox" checked={deleteCloud} onChange={e => setDeleteCloud(e.target.checked)} data-testid="wipe-anim-cloud" />
                    Also delete media files from cloud storage <span className="text-zinc-500">(irrecoverable — backup JSON only stores metadata, not the actual video bytes)</span>
                  </label>
                  <p className="text-zinc-400 mt-2">Type <span className="font-mono text-rose-200">WIPE {preview.submissions_to_wipe} ANIMATION SUBMISSIONS</span> to confirm:</p>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={e => setConfirmText(e.target.value)}
                    data-testid="wipe-anim-confirm"
                    className="w-full h-8 px-2 text-[11px] rounded-sm bg-zinc-950 border border-rose-500/40 text-rose-100 font-mono"
                    placeholder={`WIPE ${preview.submissions_to_wipe} ANIMATION SUBMISSIONS`}
                  />
                  <button
                    onClick={runExecute}
                    disabled={busy || confirmText !== `WIPE ${preview.submissions_to_wipe} ANIMATION SUBMISSIONS`}
                    data-testid="wipe-anim-execute"
                    className="h-8 px-3 text-[11px] font-semibold rounded-sm bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {busy ? 'Wiping…' : 'Step 2: EXECUTE WIPE'}
                  </button>
                </div>
              )}
            </div>
          )}

          {isWiped && (
            <div className="mt-3 border border-emerald-500/30 rounded-sm bg-emerald-500/5 p-3 text-[11px]" data-testid="wipe-anim-success">
              <p className="text-emerald-300 font-semibold">✓ Wipe complete</p>
              <p className="text-zinc-300 mt-1">{preview.submissions_deleted} submissions deleted.</p>
              {preview.cloud_files_deleted != null && (
                <p className="text-zinc-300">Cloud files deleted: {preview.cloud_files_deleted} (failed: {preview.cloud_files_failed})</p>
              )}
              <p className="text-zinc-500 mt-1">Backup: <span className="font-mono">{preview.backup_storage_key}</span></p>
              <p className="text-zinc-400 mt-2">Next: ask artists to re-upload via the new MY TASKS page (Phase 2, building next).</p>
            </div>
          )}

          {err && <p className="text-rose-400 text-[11px] mt-3" data-testid="wipe-anim-error">{err}</p>}
        </div>
      </div>
    </div>
  );
}


// ───── Backfill TDS + Bill From with optional Force-Refresh
function BackfillInvoiceTool({ api }) {
  const [force, setForce] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setRunning(true); setErr(''); setResult(null);
    try {
      const r = await api.post(`/invoices/admin/backfill-tds${force ? '?force=true' : ''}`);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed');
    }
    setRunning(false);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="tool-backfill-tds">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center justify-center">
          <Hammer className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-white font-semibold">Backfill Invoice TDS + Bill-To/Bill-From Blocks</h3>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            Adds 2% TDS deduction (Sec 194J), the studio's GSTIN bill-to block, and snapshots each vendor's GSTIN/legal-name/address onto invoices that don't have it yet.
            <br/>
            <span className="text-amber-300">Tick "Force refresh" if a vendor's GST/address changed</span> — it re-snapshots the latest billing on ALL non-paid invoices for them. Paid invoices stay frozen.
          </p>
          <label className="mt-3 flex items-center gap-2 text-[11px] text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="backfill-force-toggle"
              className="accent-amber-500"/>
            Force refresh — overwrite existing billed_from on all non-paid invoices
          </label>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={run} disabled={running} data-testid="backfill-run"
              className="h-8 px-3 text-[11px] font-semibold bg-amber-600 hover:bg-amber-500 text-white rounded-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
              {running ? 'Running…' : (force ? 'Force Refresh All' : 'Run Now')}
            </button>
            {result && (
              <span className="text-[10px] text-emerald-300 bg-emerald-950/40 border border-emerald-500/30 px-2 h-7 flex items-center rounded-sm">
                {result.fixed} fixed · {result.billed_from_added} added · {result.billed_from_refreshed} refreshed · {result.skipped_already_clean} skipped
              </span>
            )}
            {err && (
              <span className="text-[10px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-2 h-7 flex items-center rounded-sm">
                {err}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ───── Vendor billing profile editor (GSTIN, legal name, address)
function VendorBillingProfileEditor({ api }) {
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({
    vendor_legal_name: '',
    vendor_constitution: 'Partnership',
    vendor_gstin: '',
    vendor_pan: '',
    vendor_contact_person: '',
    vendor_phone: '',
    vendor_address_line_1: '',
    vendor_address_line_2: '',
    vendor_city: '',
    vendor_state_name: '',
    vendor_pin: '',
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Auto-derive PAN + state from GSTIN as the user types it
  const onGstin = (val) => {
    const v = val.toUpperCase().slice(0, 15);
    const update = { vendor_gstin: v };
    if (v.length >= 12) update.vendor_pan = v.slice(2, 12);
    setForm(prev => ({ ...prev, ...update }));
  };

  const save = async () => {
    if (!email.trim()) { setErr('Vendor email required'); return; }
    if (form.vendor_gstin && form.vendor_gstin.length !== 15) {
      setErr('GSTIN must be 15 chars'); return;
    }
    setSaving(true); setErr(''); setResult(null);
    try {
      const r = await api.put(`/users/${encodeURIComponent(email.trim())}/billing-profile`, form);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed');
    }
    setSaving(false);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="vendor-billing-editor">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center justify-center">
          <Search className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-white font-semibold">Vendor Billing Profile</h3>
          <p className="text-[11px] text-zinc-400 mt-1">
            Set a vendor's GSTIN, legal name, and bill-from address. Auto-snapshotted onto every NEW invoice they raise. Run the
            "Backfill Invoice TDS + Bill-To/Bill-From" tool above after saving here to fix existing invoices.
          </p>

          <input
            type="email" placeholder="vendor email (e.g. sidneeproductions@gmail.com)" value={email}
            onChange={e => setEmail(e.target.value)} data-testid="vendor-billing-email"
            className="mt-3 w-full h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white"
          />

          <div className="mt-2 grid grid-cols-2 gap-2">
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="Legal name (e.g. SIDNEE PRODUCTIONS)"
              value={form.vendor_legal_name} onChange={e => setForm({...form, vendor_legal_name: e.target.value})} data-testid="vendor-legal-name"/>
            <select className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" value={form.vendor_constitution}
              onChange={e => setForm({...form, vendor_constitution: e.target.value})}>
              <option>Proprietorship</option><option>Partnership</option><option>Private Limited</option><option>LLP</option><option>Public Limited</option><option>HUF</option><option>Individual</option>
            </select>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white font-mono" placeholder="GSTIN (15 chars)"
              value={form.vendor_gstin} onChange={e => onGstin(e.target.value)} maxLength={15} data-testid="vendor-gstin-input"/>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white font-mono" placeholder="PAN (auto from GSTIN)"
              value={form.vendor_pan} onChange={e => setForm({...form, vendor_pan: e.target.value.toUpperCase()})} maxLength={10}/>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="Contact person (e.g. Siddharth Mahajan)"
              value={form.vendor_contact_person} onChange={e => setForm({...form, vendor_contact_person: e.target.value})}/>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="Phone"
              value={form.vendor_phone} onChange={e => setForm({...form, vendor_phone: e.target.value})}/>
          </div>
          <input className="mt-2 w-full h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="Address line 1"
            value={form.vendor_address_line_1} onChange={e => setForm({...form, vendor_address_line_1: e.target.value})}/>
          <input className="mt-2 w-full h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="Address line 2 (locality)"
            value={form.vendor_address_line_2} onChange={e => setForm({...form, vendor_address_line_2: e.target.value})}/>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="City"
              value={form.vendor_city} onChange={e => setForm({...form, vendor_city: e.target.value})}/>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="State"
              value={form.vendor_state_name} onChange={e => setForm({...form, vendor_state_name: e.target.value})}/>
            <input className="h-9 bg-zinc-950 border border-zinc-700 rounded-sm px-3 text-xs text-white" placeholder="PIN"
              value={form.vendor_pin} onChange={e => setForm({...form, vendor_pin: e.target.value})} maxLength={6}/>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="save-vendor-billing"
              className="h-8 px-3 text-[11px] font-semibold bg-amber-600 hover:bg-amber-500 text-white rounded-sm flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
              {saving ? 'Saving…' : 'Save Billing Profile'}
            </button>
            {result && (
              <span className="text-[10px] text-emerald-300 bg-emerald-950/40 border border-emerald-500/30 px-2 h-7 flex items-center rounded-sm">
                Saved · GSTIN {result.vendor_gstin || form.vendor_gstin}
              </span>
            )}
            {err && (
              <span className="text-[10px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-2 h-7 flex items-center rounded-sm">
                {err}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ───── Recategorise + repush a single submission to its correct NAS folder
function SubmissionRecategoriser({ api }) {
  const [subId, setSubId] = useState('');
  const [newKind, setNewKind] = useState('');
  const [resetOnly, setResetOnly] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const KIND_OPTIONS = [
    { value: '', label: '— keep current kind —' },
    { value: 'character', label: 'Character (→ Character/)' },
    { value: 'environment', label: 'Environment (→ Environment/)' },
    { value: 'background', label: 'Background (→ Environment/)' },
    { value: 'prop', label: 'Prop (→ Props/)' },
    { value: 'props', label: 'Props (→ Props/)' },
  ];

  const run = async () => {
    if (!subId.trim()) return;
    setRunning(true); setErr(''); setResult(null);
    try {
      const r = await api.post('/admin/diagnostics/repair-submission-nas', {
        submission_id: subId.trim(),
        new_kind: newKind || undefined,
        reset_only: resetOnly,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed');
    }
    setRunning(false);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="tool-recategorise">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center border bg-amber-500/10 text-amber-300 border-amber-500/30">
          <Hammer className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-white font-semibold">Recategorise & Re-push Submission</h3>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            Files landed in the wrong sub-folder (e.g. environment art ended up in <span className="font-mono">Character/</span>) because the original task was tagged wrong. Pick the submission, set the correct kind, click Repair — backfill worker re-pushes it to the right NAS path within 60 seconds.
            <br/>
            <span className="text-amber-300">Note:</span> the OLD copy at the previous NAS path is <strong>not auto-deleted</strong> — clean it up in Explorer afterwards if needed.
          </p>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-2">
            <input
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              placeholder="Submission ID (uuid)"
              className="h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[11px] text-white font-mono focus:outline-none focus:border-amber-500/60"
              data-testid="recat-subid-input"
            />
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value)}
              data-testid="recat-kind-select"
              className="h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[11px] text-white focus:outline-none focus:border-amber-500/60"
            >
              {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              onClick={run}
              disabled={!subId.trim() || running}
              data-testid="recat-run-btn"
              className="h-8 px-3 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-[11px] font-semibold rounded-sm flex items-center justify-center gap-1.5"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hammer className="w-3.5 h-3.5" />}
              {running ? 'Repairing…' : 'Repair & Re-push'}
            </button>
          </div>
          <label className="mt-2 flex items-center gap-1.5 text-[10px] text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={resetOnly} onChange={e => setResetOnly(e.target.checked)} />
            <span>Reset only (just re-push to current expected path; don't change kind — useful when mapping changed)</span>
          </label>
          {err && (
            <div className="mt-3 text-[11px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-3 py-2 rounded-sm">{err}</div>
          )}
          {result && (
            <div className="mt-3 bg-zinc-950 border border-emerald-500/30 rounded-sm p-3 text-[11px] space-y-1">
              <p className="text-emerald-300 font-semibold">✅ Repair queued</p>
              <p>Stage: <span className="font-mono text-zinc-300">{result.stage}</span></p>
              <p>Old kind: <span className="font-mono text-zinc-300">{result.old_kind || '—'}</span> → New kind: <span className="font-mono text-amber-300">{result.new_kind || '—'}</span></p>
              <p>Files flagged for re-push: <span className="font-mono text-zinc-300">{result.media_flagged}</span></p>
              <p>Will land at: <span className="font-mono text-cyan-400 break-all">{result.expected_nas_dir}</span></p>
              <p className="text-zinc-500 mt-2">{result.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ───── NAS-flow diagnostic — pick a project, see why files aren't reaching NAS
function NasFlowDiagnostic({ api }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [projectsErr, setProjectsErr] = useState('');

  useEffect(() => {
    // /projects returns the user's grant-scoped list. master_admin sees all,
    // but if it ever fails (network, RBAC), surface the error so the user
    // can see why the dropdown is empty (vs. silently empty).
    api.get('/projects').then(r => {
      const list = Array.isArray(r.data) ? r.data
                 : Array.isArray(r.data?.items) ? r.data.items
                 : Array.isArray(r.data?.projects) ? r.data.projects
                 : [];
      setProjects(list);
      if (list.length === 0) setProjectsErr('No projects returned by /api/projects');
    }).catch(e => {
      setProjectsErr(e?.response?.data?.detail || e?.message || 'Failed to load projects');
    });
  }, [api]);

  const run = async () => {
    if (!projectId) return;
    setLoading(true); setErr(''); setData(null);
    try {
      const r = await api.get(`/admin/diagnostics/nas-flow/${projectId}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed');
    }
    setLoading(false);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="tool-nas-flow-diagnostic">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center border bg-cyan-500/10 text-cyan-300 border-cyan-500/30">
          <Stethoscope className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-white font-semibold">Why aren't my files reaching NAS? (Diagnostic)</h3>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            Pick a project — get a per-submission breakdown of bridge state, mapping, expected NAS path, and per-file <span className="font-mono">nas.status</span>. Plain-English verdict tells you the next action.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="diagnose-project-select"
              className="flex-1 h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:border-cyan-500/60"
            >
              <option value="">— pick a project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.is_episode ? `(ep of ${p.show_name})` : ''}
                </option>
              ))}
            </select>
            <input
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="…or paste project_id directly"
              className="w-64 h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white font-mono focus:outline-none focus:border-cyan-500/60"
              data-testid="diagnose-project-id-input"
            />
            <button
              onClick={run}
              disabled={!projectId || loading}
              data-testid="run-diagnostic-btn"
              className="h-8 px-3 bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-[11px] font-semibold rounded-sm flex items-center gap-1.5"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              {loading ? 'Diagnosing…' : 'Diagnose'}
            </button>
          </div>
          {projectsErr && (
            <p className="mt-2 text-[10px] text-amber-300">⚠️ Project list: {projectsErr} — paste a project_id manually instead.</p>
          )}
          {err && (
            <div className="mt-3 text-[11px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-3 py-2 rounded-sm">{err}</div>
          )}
          {data && (
            <div className="mt-4 space-y-3" data-testid="diagnostic-result">
              {/* Verdict banner */}
              <div className="bg-zinc-950 border border-cyan-500/30 rounded-sm p-3">
                <p className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1">Verdict</p>
                <p className="text-sm text-white">{data.verdict}</p>
              </div>
              {/* Quick facts */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <Fact label="Bridge" value={data.bridge.live ? '🟢 Live' : '🔴 Offline'} />
                <Fact label="Mapping" value={data.mapping.exists ? '🟢 Yes' : '🔴 No'} />
                <Fact label="Mapped to" value={data.mapping.resolved_relative_root || '—'} mono />
                <Fact label="Pending Backfill" value={data.pending_backfill_for_project} />
              </div>
              {/* Status breakdown */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
                <p className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2">Media NAS Status (across all submissions)</p>
                <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(data.media_status_breakdown || {}).map(([k, v]) => (
                    <div key={k} className={`px-2 py-1.5 rounded text-[10px] ${v > 0 ? (k === 'ok' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-300') : 'bg-zinc-900 text-zinc-600'}`}>
                      <span className="font-mono">{k}</span>: <span className="font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Recent submissions */}
              {data.recent_submissions?.length > 0 && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
                  <p className="px-3 py-2 text-[11px] text-zinc-400 uppercase tracking-wider border-b border-zinc-800">Recent Submissions ({data.recent_submissions.length})</p>
                  <div className="max-h-96 overflow-y-auto divide-y divide-zinc-800">
                    {data.recent_submissions.map(s => (
                      <details key={s.id} className="px-3 py-2">
                        <summary className="cursor-pointer flex items-center gap-2 text-[11px]">
                          <span className="text-zinc-300 font-medium">{s.stage}</span>
                          {s.kind && <span className="text-zinc-500">· {s.kind}</span>}
                          {s.parent_code && <span className="text-violet-300 font-mono">· {s.parent_code}</span>}
                          <span className="text-zinc-500 ml-auto">by {s.submitter_name}{s.submitter_handle ? <span className="text-cyan-400 ml-1">@{s.submitter_handle}</span> : null}</span>
                          <span className={`text-[9px] px-2 py-0.5 rounded-full ${s.media.every(m => m.nas_status === 'ok') ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{s.media.length} files</span>
                        </summary>
                        <div className="mt-2 ml-3 space-y-1">
                          <p className="text-[10px] text-zinc-500">expected: <span className="font-mono text-cyan-400">{s.expected_nas_dir}</span></p>
                          {s.media.map((m, i) => (
                            <div key={i} className="flex items-center gap-2 text-[10px] bg-zinc-900 rounded px-2 py-1">
                              <span className={`w-2 h-2 rounded-full ${m.nas_status === 'ok' ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                              <span className="font-mono text-zinc-300 truncate flex-1">{m.filename}</span>
                              <span className={`px-1.5 py-0.5 rounded ${m.nas_status === 'ok' ? 'bg-emerald-950/50 text-emerald-300' : 'bg-amber-950/50 text-amber-300'}`}>{m.nas_status}</span>
                              {m.retry_count > 0 && <span className="text-zinc-500">×{m.retry_count}</span>}
                              <span className="text-zinc-600">{m.size_kb}KB</span>
                            </div>
                          ))}
                          {s.media.some(m => m.nas_error) && (
                            <p className="text-[10px] text-rose-400">errors: {s.media.filter(m => m.nas_error).map(m => m.nas_error).join(' · ')}</p>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value, mono }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
      <p className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xs text-white mt-0.5 ${mono ? 'font-mono break-all' : ''}`}>{value}</p>
    </div>
  );
}
